import { Spacing } from '@persona-id/design-tokens';
import { lowerCase, startCase, upperFirst } from 'lodash/fp';
import styled, { ThemeProvider } from 'styled-components';

import Accordion from 'dashboard/components/Accordion/Accordion';
import { EXTRACTOR_TYPE_TO_DISPLAY_NAME } from 'dashboard/components/ExtractionPanel/constants';
import { ExtractionCategory } from 'dashboard/components/ExtractionPanel/util';
import { ExtractionType, GuidedPreset } from 'dashboard/models/document';
import {
  ExtractionGroup,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import { ExtractionDetailWithToggle } from 'dashboard/views/DashboardDocumentViewerView/ExtractionDetail';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import defaultTheme from 'design-system/theme';

const Container = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const DetailsContainer = styled.div`
  padding: ${Spacing[3]} ${Spacing[5]};
`;

interface ExtractionCardProps {
  extractionGroups: ExtractionGroup[];
  focusedExtractions: FocusedExtraction[];
  setFocusedExtractions: (focusedExtractions: FocusedExtraction[]) => void;
}

export const ExtractionCard = ({
  extractionGroups,
  focusedExtractions,
  setFocusedExtractions,
}: ExtractionCardProps) => {
  const {
    fieldName,
    fieldIdentifier,
    extractionType,
    extractorType,
    sourceValue,
    matchLevelMinimum,
    guidedPreset,
    filtered,
  } = extractionGroups[0];

  const extractionGroupsWtihExtractions = extractionGroups.filter(
    (extractionGroup) => extractionGroup.numResults > 0,
  );
  const disabled = extractionGroupsWtihExtractions.length === 0 || filtered;

  let comparisonSource = undefined;
  switch (guidedPreset) {
    case GuidedPreset.AddressLoose:
    case GuidedPreset.AddressStrict:
      comparisonSource = ExtractionCategory.Address;
      break;
    case GuidedPreset.NameLoose:
    case GuidedPreset.NameStrict:
      comparisonSource = ExtractionCategory.Name;
      break;
    case GuidedPreset.GenericLoose:
    case GuidedPreset.GenericLooseAi:
    case GuidedPreset.GenericStrict:
      comparisonSource = fieldIdentifier;
      break;
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container>
        <Accordion
          title={
            <>
              <Spacer height={2} />
              {upperFirst(lowerCase(fieldName))} ({extractionGroupsWtihExtractions.length})
              {extractorType && (
                <Paragraph size='sm'>{EXTRACTOR_TYPE_TO_DISPLAY_NAME[extractorType]}</Paragraph>
              )}
              {matchLevelMinimum && (
                <Paragraph size='sm'>Match requirement: {startCase(matchLevelMinimum)}</Paragraph>
              )}
              <Spacer height={2} />
            </>
          }
          disabled={disabled}
          menuItem
          menuItemBorder={false}
          rounded
          disabledHideCaret
        >
          {extractionType === ExtractionType.Guided && (
            <DetailsContainer>
              <Paragraph>
                Comparison source:{' '}
                {comparisonSource ? upperFirst(lowerCase(comparisonSource)) : 'Custom'}
              </Paragraph>
              <Paragraph color='primary'>{sourceValue || 'None'}</Paragraph>
            </DetailsContainer>
          )}
          {!disabled &&
            extractionGroups.map((extractionGroup) => (
              <ExtractionDetailWithToggle
                key={`${extractionGroup.fieldName}-${extractionGroup.fieldIdentifier}-${extractionGroup.extractionValue}`}
                extractionGroup={extractionGroup}
                focusedExtractions={focusedExtractions}
                setFocusedExtractions={setFocusedExtractions}
              />
            ))}
        </Accordion>
      </Container>
    </ThemeProvider>
  );
};

export default ExtractionCard;
